import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import { AntDesign, Ionicons } from "@expo/vector-icons";

import Colors from "../../../../Resources/Color";
import Fonts from "../../../../Resources/Fonts";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import Tooltip from "../../../Components/Tooltip";
import { useMainViewScroll } from "../../../platform/mainViewScrollContext";
import {
  getLocalizedFoodName,
} from "../../foods/foodCatalog";
import AddFoodModal from "../../foods/editor/AddFoodModal";
import {
  INITIAL_ADD_FOOD_MODAL_SESSION_STATE,
  reduceAddFoodModalSession,
} from "../../foods/editor/addFoodModalSession";
import {
  buildCanonicalFoodFilters,
  FOOD_SEARCH_SURFACE_PICKER,
} from "../../foods/foodSearchService";
import { useCanonicalFoodSearch } from "../../foods/useCanonicalFoodSearch";
import { FoodPickerPanel } from "../../shared/foodPicker/FoodPickerPanel";
import { getEditorFoodSourceLabel } from "../../shared/editorFoodSourceLabel";
import { SelectedFoodRow } from "../../shared/foodRows/SelectedFoodRow";
import { getSelectedRowDeleteTooltipKey } from "../../shared/foodRows/selectedRowDeleteTooltip";
import {
  getNutritionConflictDescription,
  getNutritionConflictSummary,
} from "../../shared/nutritionConflictWarnings";
import {
  INGREDIENT_DATABASE_OPTIONS,
  INGREDIENT_SORT_OPTIONS,
  formatQuantityInGramsText,
  getFoodStableKey,
  getInitialFoodQuantityInGrams,
  getIngredientQuantityInGrams,
  getIngredientSelectedUnitKey,
  getIngredientSourceLabel,
  getNutritionTotalsForQuantity,
  getQuantityUnitOptions,
} from "../../recipes/recipeLogic";
import {
  buildCanonicalPickerResults,
  matchesPickerBrowserFilter,
  PICKER_FILTER_ALL,
  PICKER_FILTER_CATALOG,
  PICKER_FILTER_CREATED_BY_ME,
  PICKER_FILTER_EQUIVALENTS,
  PICKER_FILTER_RECIPES,
} from "../../shared/foodPicker/pickerBrowser";
import { getMealOptionTotals } from "../nutritionPlanLogic";
import {
  focusNutritionPlanFoodPickerSearchInput,
  restoreNutritionPlanFoodPickerSearchAfterSelection,
} from "./nutritionPlanFoodPickerFocusUtils";
import {
  NUTRITION_PLAN_INLINE_PICKER_KIND_EQUIVALENCE,
  isNutritionPlanEquivalentPickerOpen,
} from "./nutritionPlanFoodPickerScopeUtils";
import { useFoodPickerRevealInMainView } from "../../shared/foodPicker/foodPickerReveal";

const EQUIVALENCE_TREE_VISUAL_GAP = 12;
const EQUIVALENCE_TREE_CARD_VERTICAL_PADDING = EQUIVALENCE_TREE_VISUAL_GAP / 2;

function SelectedEquivalenceCard({
  client,
  equivalence,
  updateEquivalenceQuantity,
  updateEquivalenceUnit,
  removeEquivalence,
  compactLayout,
  isLast,
  t,
}) {
  const warningTooltip = getNutritionConflictDescription(
    getNutritionConflictSummary({
      client,
      source: equivalence,
      t,
    }),
  );

  return (
    <SelectedFoodRow
      title={equivalence.name}
      subtitle={getEditorFoodSourceLabel(equivalence, t)}
      quantityValue={String(equivalence.quantity ?? "")}
      onChangeQuantity={(value) => updateEquivalenceQuantity(equivalence.id, value)}
      unitOptions={getQuantityUnitOptions(equivalence, { t })}
      selectedUnitKey={getIngredientSelectedUnitKey(equivalence)}
      onSelectUnit={(nextUnitKey) =>
        updateEquivalenceUnit(equivalence.id, nextUnitKey)
      }
      referenceText={formatQuantityInGramsText(getIngredientQuantityInGrams(equivalence))}
      totals={getNutritionTotalsForQuantity(
        equivalence,
        getIngredientQuantityInGrams(equivalence),
      )}
      compactLayout={compactLayout}
      isLast={isLast}
      chromeVariant="transparent"
      showDivider={false}
      showNutritionSummaryCalories={false}
      warningTooltip={warningTooltip}
      isAutoCalculated={equivalence.isAutoCalculated === true}
      autoCalculatedTooltip={t(
        "nutritionAutoCalculatedEquivalentTooltip-string",
        "Auto-calculated to match energy",
      )}
      leadingSlot={
        <View style={styles.equivalenceIconWrap}>
          <Ionicons name="git-compare-outline" size={14} color={Colors.bubbleColor} />
        </View>
      }
      actionSlot={
        <View style={[styles.rowActionGroup, styles.rowActionGroupCompact]}>
          <Tooltip
            content={getSelectedRowDeleteTooltipKey({
              entityKind: "equivalent",
            })}
            placement="top"
          >
            <View>
              <HoverableTouchableOpacity
                activeOpacity={0.85}
                onPress={() => removeEquivalence(equivalence.id)}
                style={[styles.removeIngredientButton, styles.equivalenceRemoveButton]}
                hoverStyle={styles.removeIngredientButtonHover}
              >
                <Ionicons
                  name="trash-outline"
                  size={16}
                  color={Colors.darkgrayColor}
                />
              </HoverableTouchableOpacity>
            </View>
          </Tooltip>
        </View>
      }
      containerStyle={styles.selectedEquivalenceCard}
    />
  );
}

function EquivalenceTree({
  client,
  item,
  equivalences,
  compactLayout,
  updateEquivalenceQuantity,
  updateEquivalenceUnit,
  removeEquivalence,
  optionId,
  t,
}) {
  if (equivalences.length === 0) {
    return null;
  }

  return (
    <View style={styles.selectedIngredientEquivalencesRow}>
      {equivalences.map((equivalence, equivalenceIndex) => {
        const isFirstEquivalence = equivalenceIndex === 0;
        const isLastEquivalence = equivalenceIndex === equivalences.length - 1;

        return (
          <View
            key={equivalence.id}
            style={[
              styles.equivalenceTreeRowContainer,
              isLastEquivalence ? styles.equivalenceTreeRowContainerLast : null,
            ]}
          >
            <View style={styles.equivalenceTreeLineContainer}>
              <View
                style={[
                  styles.equivalenceTreeVerticalLine,
                  isFirstEquivalence ? styles.equivalenceTreeVerticalLineFirst : null,
                  isLastEquivalence ? styles.equivalenceTreeVerticalLineLast : null,
                ]}
              />
              <View style={styles.equivalenceTreeHorizontalLine} />
              <View style={styles.equivalenceTreeDot} />
            </View>
            <View style={styles.equivalenceTreeCardWrap}>
              <SelectedEquivalenceCard
                client={client}
                equivalence={equivalence}
                updateEquivalenceQuantity={(equivalenceId, value) =>
                  updateEquivalenceQuantity(optionId, item.id, equivalenceId, value)
                }
                updateEquivalenceUnit={(equivalenceId, nextUnitKey) =>
                  updateEquivalenceUnit(optionId, item.id, equivalenceId, nextUnitKey)
                }
                removeEquivalence={(equivalenceId) =>
                  removeEquivalence(optionId, item.id, equivalenceId)
                }
                compactLayout={compactLayout}
                isLast={isLastEquivalence}
                t={t}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
}

function MealFoodRowContent({
  client,
  mealId,
  item,
  index,
  itemCount,
  optionId,
  compactLayout,
  equivalenceTargetId,
  setEquivalenceTargetId,
  onInlinePickerOpenChange,
  updateItemQuantity,
  updateItemUnit,
  removeItem,
  updateEquivalenceQuantity,
  updateEquivalenceUnit,
  removeEquivalence,
  equivalencePickerPanelProps,
  equivalencePickerPanelRootRef,
  isMainAddFoodTarget,
  mainPickerPanelProps,
  mainPickerPanelRootRef,
  closeMainFoodPicker,
  dragHandleProps = null,
  t,
}) {
  const isEquivalenceTarget = equivalenceTargetId === item.id;
  const equivalences = Array.isArray(item.equivalences) ? item.equivalences : [];
  const warningTooltip = getNutritionConflictDescription(
    getNutritionConflictSummary({
      client,
      source: item,
      t,
    }),
  );

  return (
    <SelectedFoodRow
      title={item.name}
      subtitle={getEditorFoodSourceLabel(item, t)}
      quantityValue={String(item.quantity ?? "")}
      onChangeQuantity={(value) => updateItemQuantity(optionId, item.id, value)}
      unitOptions={getQuantityUnitOptions(item, { t })}
      selectedUnitKey={getIngredientSelectedUnitKey(item)}
      onSelectUnit={(nextUnitKey) => updateItemUnit(optionId, item.id, nextUnitKey)}
      referenceText={formatQuantityInGramsText(getIngredientQuantityInGrams(item))}
      totals={getNutritionTotalsForQuantity(item, getIngredientQuantityInGrams(item))}
      compactLayout={compactLayout}
      isLast={index === itemCount - 1}
      chromeVariant="transparent"
      warningTooltip={warningTooltip}
      dragHandleProps={dragHandleProps}
      actionSlot={
        <View
          style={[
            styles.rowActionGroup,
            compactLayout ? styles.rowActionGroupCompact : null,
          ]}
        >
          <Tooltip content="addAnotherOption-string" placement="top">
            <View>
              <HoverableTouchableOpacity
                activeOpacity={0.85}
                onPress={() => {
                  const nextEquivalenceTargetId =
                    equivalenceTargetId === item.id ? null : item.id;
                  setEquivalenceTargetId(nextEquivalenceTargetId);
                  onInlinePickerOpenChange?.(
                    {
                      mealId,
                      optionId,
                      kind: NUTRITION_PLAN_INLINE_PICKER_KIND_EQUIVALENCE,
                      itemId: item.id,
                    },
                    nextEquivalenceTargetId != null,
                  );
                }}
                style={[
                  styles.rowActionButton,
                  isEquivalenceTarget ? styles.rowActionButtonActive : null,
                ]}
                hoverStyle={styles.rowActionButtonHover}
              >
                <Ionicons
                  name="git-compare-outline"
                  size={15}
                  color={Colors.bubbleColor}
                />
              </HoverableTouchableOpacity>
            </View>
          </Tooltip>
          <Tooltip
            content={getSelectedRowDeleteTooltipKey({
              entityKind: "food",
              equivalenceCount: equivalences.length,
            })}
            placement="top"
          >
            <View>
              <HoverableTouchableOpacity
                activeOpacity={0.85}
                onPress={() => removeItem(optionId, item.id)}
                style={styles.removeIngredientButton}
                hoverStyle={styles.removeIngredientButtonHover}
              >
                <Ionicons
                  name="trash-outline"
                  size={16}
                  color={Colors.darkgrayColor}
                />
              </HoverableTouchableOpacity>
            </View>
          </Tooltip>
        </View>
      }
      footer={
        <>
          <EquivalenceTree
            client={client}
            item={item}
            equivalences={equivalences}
            compactLayout={compactLayout}
            updateEquivalenceQuantity={updateEquivalenceQuantity}
            updateEquivalenceUnit={updateEquivalenceUnit}
            removeEquivalence={removeEquivalence}
            optionId={optionId}
            t={t}
          />
          {isEquivalenceTarget ? (
            <FoodPickerPanel
              {...equivalencePickerPanelProps}
              panelRootRef={equivalencePickerPanelRootRef}
              title={t("addEquivalent-string")}
              subtitle={t("addingEquivalentTo-string", {
                name: item.name,
              })}
              onClose={() => {
                setEquivalenceTargetId(null);
                onInlinePickerOpenChange?.(
                  {
                    mealId,
                    optionId,
                    kind: NUTRITION_PLAN_INLINE_PICKER_KIND_EQUIVALENCE,
                    itemId: item.id,
                  },
                  false,
                );
              }}
              isInline={true}
            />
          ) : null}
          {isMainAddFoodTarget ? (
            <FoodPickerPanel
              {...mainPickerPanelProps}
              panelRootRef={mainPickerPanelRootRef}
              title={t("addFood-string")}
              subtitle={t(
                "nutritionPlanAddPickerSubtitle-string",
                "Search foods, equivalents, and recipes and add them to this option.",
              )}
              onClose={closeMainFoodPicker}
              isInline={true}
              topMarginOverride={4}
              bottomMarginOverride={-12}
            />
          ) : null}
        </>
      }
      trimBottomPadding={isEquivalenceTarget || isMainAddFoodTarget}
    />
  );
}

function DraggableMealFoodRow({
  client,
  mealId,
  item,
  index,
  itemCount,
  optionId,
  compactLayout,
  equivalenceTargetId,
  setEquivalenceTargetId,
  onInlinePickerOpenChange,
  updateItemQuantity,
  updateItemUnit,
  removeItem,
  updateEquivalenceQuantity,
  updateEquivalenceUnit,
  removeEquivalence,
  equivalencePickerPanelProps,
  equivalencePickerPanelRootRef,
  isMainAddFoodTarget,
  mainPickerPanelProps,
  mainPickerPanelRootRef,
  closeMainFoodPicker,
  isDraggingActiveItem = false,
  registerFoodRowNode,
  getFoodDragHandleProps,
  t,
}) {
  const dragHandleProps = useMemo(
    () => ({
      dragHandleProps: getFoodDragHandleProps?.({
        mealId,
        optionId,
        itemId: item.id,
        sourceIndex: index,
      }),
      isDragging: isDraggingActiveItem,
    }),
    [
      getFoodDragHandleProps,
      index,
      isDraggingActiveItem,
      item.id,
      mealId,
      optionId,
    ],
  );

  return (
    <div
      ref={(node) => {
        registerFoodRowNode?.(mealId, optionId, item.id, node);
      }}
    >
      <MealFoodRowContent
        client={client}
        mealId={mealId}
        item={item}
        index={index}
        itemCount={itemCount}
        optionId={optionId}
        compactLayout={compactLayout}
        equivalenceTargetId={equivalenceTargetId}
        setEquivalenceTargetId={setEquivalenceTargetId}
        onInlinePickerOpenChange={onInlinePickerOpenChange}
        updateItemQuantity={updateItemQuantity}
        updateItemUnit={updateItemUnit}
        removeItem={removeItem}
        updateEquivalenceQuantity={updateEquivalenceQuantity}
        updateEquivalenceUnit={updateEquivalenceUnit}
        removeEquivalence={removeEquivalence}
        equivalencePickerPanelProps={equivalencePickerPanelProps}
        equivalencePickerPanelRootRef={equivalencePickerPanelRootRef}
        isMainAddFoodTarget={isMainAddFoodTarget}
        mainPickerPanelProps={mainPickerPanelProps}
        mainPickerPanelRootRef={mainPickerPanelRootRef}
        closeMainFoodPicker={closeMainFoodPicker}
        dragHandleProps={dragHandleProps}
        t={t}
      />
    </div>
  );
}

function MealFoodPlaceholder({ height = 0 }) {
  return (
    <View
      style={[
        styles.foodPlaceholder,
        height > 0 ? { height } : null,
      ]}
    />
  );
}

function EmptyMealOptionDropZone({
  visible = false,
  compactLayout,
  isActive = false,
  t,
}) {
  if (!visible) {
    return null;
  }

  return (
    <View
      style={[
        styles.emptyDropZone,
        compactLayout ? styles.emptyDropZoneCompact : null,
        isActive ? styles.emptyDropZoneActive : null,
      ]}
    >
      <View
        style={[
          styles.emptyDropZoneIconWrap,
          isActive ? styles.emptyDropZoneIconWrapActive : null,
        ]}
      >
        <Ionicons name="move-outline" size={16} color={Colors.bubbleColor} />
      </View>
      <Text style={styles.emptyDropZoneTitle}>
        {t("nutritionPlanDropFoodHere-string", "Drop food here")}
      </Text>
    </View>
  );
}

export function NutritionPlanMealOptionCard({
  coach,
  client = null,
  t,
  language,
  mealId,
  option,
  optionUiState = null,
  onOptionUiStateChange,
  openInlinePickerOwner = null,
  isDefaultOption,
  compactLayout,
  showHeader = true,
  mealPlanEmbed = false,
  foodPickerOpen: controlledFoodPickerOpen,
  onFoodPickerOpenChange,
  onInlinePickerOpenChange,
  isFoodDragActive = false,
  activeFoodDragItemId = "",
  foodDragTarget = null,
  foodDragPlaceholderHeight = 0,
  registerFoodOptionBodyNode,
  registerFoodRowNode,
  getFoodDragHandleProps,
  updateOptionName,
  removeOption,
  onAddFood,
  updateItemQuantity,
  updateItemUnit,
  removeItem,
  updateEquivalenceQuantity,
  updateEquivalenceUnit,
  removeEquivalence,
}) {
  const { revealInMainView } = useMainViewScroll();
  const searchInputRef = useRef(null);
  const inputPressingRef = useRef(false);
  const searchTextRef = useRef("");
  const mainPickerPanelRootRef = useRef(null);
  const equivalencePickerPanelRootRef = useRef(null);
  const [internalSearchText, setInternalSearchText] = useState("");
  const [internalSortKey, setInternalSortKey] = useState("default");
  const [internalPickerFilterKey, setInternalPickerFilterKey] =
    useState(PICKER_FILTER_ALL);
  const [internalEquivalenceDatabaseKey, setInternalEquivalenceDatabaseKey] =
    useState(PICKER_FILTER_ALL);
  const [internalFoodPickerOpen, setInternalFoodPickerOpen] = useState(false);
  const [internalEquivalenceTargetId, setInternalEquivalenceTargetId] =
    useState(null);
  const [addFoodModalSession, dispatchAddFoodModalSession] = useReducer(
    reduceAddFoodModalSession,
    INITIAL_ADD_FOOD_MODAL_SESSION_STATE,
  );
  const searchText = optionUiState?.searchText ?? internalSearchText;
  const sortKey = optionUiState?.sortKey ?? internalSortKey;
  const pickerFilterKey =
    optionUiState?.pickerFilterKey ?? internalPickerFilterKey;
  const equivalenceDatabaseKey =
    optionUiState?.equivalenceDatabaseKey ?? internalEquivalenceDatabaseKey;
  const equivalenceTargetId =
    optionUiState?.equivalenceTargetId ?? internalEquivalenceTargetId;
  searchTextRef.current = searchText;
  const isEquivalencePickerOwner = isNutritionPlanEquivalentPickerOpen({
    openInlinePickerOwner,
    mealId,
    optionId: option?.id,
    itemId: equivalenceTargetId,
  });

  const isPickerControlled =
    typeof controlledFoodPickerOpen === "boolean" &&
    typeof onFoodPickerOpenChange === "function";
  const isFoodPickerOpen = isPickerControlled
    ? controlledFoodPickerOpen
    : internalFoodPickerOpen;
  const setFoodPickerOpen = isPickerControlled
    ? onFoodPickerOpenChange
    : setInternalFoodPickerOpen;
  const setSearchText = useCallback(
    (value) => {
      const nextValue =
        typeof value === "function" ? value(searchText) : value;
      const normalizedNextValue = String(nextValue ?? "");
      if (typeof onOptionUiStateChange === "function") {
        onOptionUiStateChange((current) =>
          current.searchText === normalizedNextValue
            ? current
            : {
                ...current,
                searchText: normalizedNextValue,
              },
        );
        return;
      }

      setInternalSearchText(normalizedNextValue);
    },
    [onOptionUiStateChange, searchText],
  );
  const setSortKey = useCallback(
    (value) => {
      const nextValue = typeof value === "function" ? value(sortKey) : value;
      const normalizedNextValue = String(nextValue ?? "default") || "default";
      if (typeof onOptionUiStateChange === "function") {
        onOptionUiStateChange((current) =>
          current.sortKey === normalizedNextValue
            ? current
            : {
                ...current,
                sortKey: normalizedNextValue,
              },
        );
        return;
      }

      setInternalSortKey(normalizedNextValue);
    },
    [onOptionUiStateChange, sortKey],
  );
  const setPickerFilterKey = useCallback(
    (value) => {
      const nextValue =
        typeof value === "function" ? value(pickerFilterKey) : value;
      const normalizedNextValue =
        String(nextValue ?? PICKER_FILTER_ALL) || PICKER_FILTER_ALL;
      if (typeof onOptionUiStateChange === "function") {
        onOptionUiStateChange((current) =>
          current.pickerFilterKey === normalizedNextValue
            ? current
            : {
                ...current,
                pickerFilterKey: normalizedNextValue,
              },
        );
        return;
      }

      setInternalPickerFilterKey(normalizedNextValue);
    },
    [onOptionUiStateChange, pickerFilterKey],
  );
  const setEquivalenceDatabaseKey = useCallback(
    (value) => {
      const nextValue =
        typeof value === "function"
          ? value(equivalenceDatabaseKey)
          : value;
      const normalizedNextValue =
        String(nextValue ?? PICKER_FILTER_ALL) || PICKER_FILTER_ALL;
      if (typeof onOptionUiStateChange === "function") {
        onOptionUiStateChange((current) =>
          current.equivalenceDatabaseKey === normalizedNextValue
            ? current
            : {
                ...current,
                equivalenceDatabaseKey: normalizedNextValue,
              },
        );
        return;
      }

      setInternalEquivalenceDatabaseKey(normalizedNextValue);
    },
    [equivalenceDatabaseKey, onOptionUiStateChange],
  );
  const setEquivalenceTargetId = useCallback(
    (value) => {
      const nextValue =
        typeof value === "function"
          ? value(equivalenceTargetId)
          : value;
      const normalizedNextValue =
        typeof nextValue === "string" && nextValue.trim().length > 0
          ? nextValue.trim()
          : null;
      if (typeof onOptionUiStateChange === "function") {
        onOptionUiStateChange((current) =>
          current.equivalenceTargetId === normalizedNextValue
            ? current
            : {
                ...current,
                equivalenceTargetId: normalizedNextValue,
              },
        );
        return;
      }

      setInternalEquivalenceTargetId(normalizedNextValue);
    },
    [equivalenceTargetId, onOptionUiStateChange],
  );

  const scheduleSearchSelection = useCallback((applySelection) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(applySelection);
      return;
    }

    setTimeout(applySelection, 0);
  }, []);

  const coachFoods = Array.isArray(coach?.nutritionStore?.sortedFoods)
    ? coach.nutritionStore.sortedFoods
    : [];
  const recipes = Array.isArray(coach?.nutritionStore?.sortedRecipes)
    ? coach.nutritionStore.sortedRecipes
    : [];
  const equivalents = Array.isArray(coach?.nutritionStore?.sortedEquivalents)
    ? coach.nutritionStore.sortedEquivalents
    : [];

  const sortOptions = useMemo(
    () =>
      INGREDIENT_SORT_OPTIONS.map((sortOption) => ({
        ...sortOption,
        label: t(sortOption.labelKey),
      })),
    [t],
  );
  const selectedSortOption =
    sortOptions.find((sortOption) => sortOption.key === sortKey) || sortOptions[0];
  const equivalenceDatabaseOptions = useMemo(
    () =>
      INGREDIENT_DATABASE_OPTIONS.map((databaseOption) => ({
        ...databaseOption,
        label: t(databaseOption.labelKey),
      })),
    [t],
  );
  const mainFilterOptions = useMemo(
    () => [
      {
        key: PICKER_FILTER_ALL,
        label: t("all-string"),
      },
      {
        key: PICKER_FILTER_CREATED_BY_ME,
        label: t("createdByMe-string"),
      },
      {
        key: PICKER_FILTER_CATALOG,
        label: t("catalogSourceFoodDatabase-string", "Food database"),
      },
      {
        key: PICKER_FILTER_EQUIVALENTS,
        label: t("equivalents-string"),
      },
      {
        key: PICKER_FILTER_RECIPES,
        label: t("recipes-string"),
      },
    ],
    [t],
  );
  const selectedMainFilterOption =
    mainFilterOptions.find((filterOption) => filterOption.key === pickerFilterKey) ||
    mainFilterOptions[0];
  const selectedEquivalenceDatabaseOption =
    equivalenceDatabaseOptions.find(
      (databaseOption) => databaseOption.key === equivalenceDatabaseKey,
    ) || equivalenceDatabaseOptions[0];
  const inlineFoodFilters = useMemo(() => {
    if (equivalenceDatabaseKey === "createdByMe") {
      return buildCanonicalFoodFilters({ source: "coach" });
    }

    if (equivalenceDatabaseKey === "catalog") {
      return buildCanonicalFoodFilters({ source: "catalog" });
    }

    return buildCanonicalFoodFilters();
  }, [equivalenceDatabaseKey]);
  const mainFoodFilters = useMemo(() => {
    if (pickerFilterKey === PICKER_FILTER_CREATED_BY_ME) {
      return buildCanonicalFoodFilters({ source: "coach" });
    }

    if (pickerFilterKey === PICKER_FILTER_CATALOG) {
      return buildCanonicalFoodFilters({ source: "catalog" });
    }

    return buildCanonicalFoodFilters();
  }, [pickerFilterKey]);
  const inlineFoodSearch = useCanonicalFoodSearch({
    coachFoods,
    query: searchText,
    filters: inlineFoodFilters,
    sortField: selectedSortOption?.field,
    pageSize: 8,
    surface: FOOD_SEARCH_SURFACE_PICKER,
    language,
  });
  const mainFoodSearch = useCanonicalFoodSearch({
    coachFoods,
    query: searchText,
    filters: mainFoodFilters,
    sortField: selectedSortOption?.field,
    pageSize: 8,
    surface: FOOD_SEARCH_SURFACE_PICKER,
    language,
  });
  const inlineFoodResults = inlineFoodSearch.items;
  const mainPickerResults = useMemo(() => {
    return buildCanonicalPickerResults(
      {
        foods: mainFoodSearch.items,
        equivalents,
        recipes,
      },
      {
        language,
        t,
        searchText,
        filterKey: pickerFilterKey,
        sortField: selectedSortOption?.field,
        limit: 8,
      },
    );
  }, [
    equivalents,
    language,
    mainFoodSearch.items,
    pickerFilterKey,
    recipes,
    searchText,
    selectedSortOption?.field,
    t,
  ]);

  const focusSearchInput = useCallback(
    ({ selectText = false, ignorePressGuard = false } = {}) => {
      focusNutritionPlanFoodPickerSearchInput({
        input: searchInputRef.current,
        isInputPressing: inputPressingRef.current,
        ignorePressGuard,
        selectText,
        searchText: searchTextRef.current,
        scheduleSelection: scheduleSearchSelection,
      });
    },
    [scheduleSearchSelection],
  );
  const restoreSearchAfterPickerSelection = useCallback(() => {
    restoreNutritionPlanFoodPickerSearchAfterSelection({
      focusSearchInput,
    });
  }, [focusSearchInput]);
  useFoodPickerRevealInMainView({
    isVisible: isFoodPickerOpen && !equivalenceTargetId,
    panelRootRef: mainPickerPanelRootRef,
    revealInMainView,
    focusSearchInput,
  });
  useFoodPickerRevealInMainView({
    isVisible: Boolean(equivalenceTargetId),
    panelRootRef: equivalencePickerPanelRootRef,
    revealInMainView,
    focusSearchInput,
  });

  const openMainFoodPicker = useCallback(() => {
    if (isFoodPickerOpen) {
      return;
    }

    setFoodPickerOpen(true);
  }, [isFoodPickerOpen, setFoodPickerOpen]);

  const closeEquivalentPicker = useCallback(
    (itemId = null) => {
      setEquivalenceTargetId(null);
      if (!itemId) {
        return;
      }

      onInlinePickerOpenChange?.(
        {
          mealId,
          optionId: option?.id,
          kind: NUTRITION_PLAN_INLINE_PICKER_KIND_EQUIVALENCE,
          itemId,
        },
        false,
      );
    },
    [mealId, onInlinePickerOpenChange, option?.id, setEquivalenceTargetId],
  );

  useEffect(() => {
    if (!equivalenceTargetId || isEquivalencePickerOwner) {
      return;
    }

    setEquivalenceTargetId(null);
  }, [equivalenceTargetId, isEquivalencePickerOwner, setEquivalenceTargetId]);

  useEffect(() => {
    if (!isFoodDragActive || !equivalenceTargetId) {
      return;
    }

    closeEquivalentPicker(equivalenceTargetId);
  }, [closeEquivalentPicker, equivalenceTargetId, isFoodDragActive]);

  const handleAddFood = useCallback(
    (food) => {
      onAddFood({
        optionId: option.id,
        selection: {
          entityKind: "food",
          sourceRecord: food,
        },
        parentItemId: equivalenceTargetId,
      });
      restoreSearchAfterPickerSelection();
    },
    [equivalenceTargetId, onAddFood, option.id, restoreSearchAfterPickerSelection],
  );

  const handleFoodCreated = useCallback(
    (savedFood) => {
      dispatchAddFoodModalSession({ type: "close" });
      onAddFood({
        optionId: option.id,
        selection: {
          entityKind: "food",
          sourceRecord: savedFood,
        },
        parentItemId: equivalenceTargetId,
      });
      setSearchText("");
      focusSearchInput();
    },
    [equivalenceTargetId, focusSearchInput, onAddFood, option.id, setSearchText],
  );
  const openAddFoodModal = useCallback(() => {
    dispatchAddFoodModalSession({ type: "open" });
  }, []);
  const closeAddFoodModal = useCallback(() => {
    dispatchAddFoodModalSession({ type: "close" });
  }, []);
  const handleAddPickerItem = useCallback(
    (pickerItem) => {
      onAddFood({
        optionId: option.id,
        selection: pickerItem,
        parentItemId: equivalenceTargetId,
      });
      restoreSearchAfterPickerSelection();
    },
    [equivalenceTargetId, onAddFood, option.id, restoreSearchAfterPickerSelection],
  );

  const equivalencePickerPanelProps = useMemo(
    () => ({
      t,
      searchInputRef,
      inputPressingRef,
      focusSearchInput,
      searchText,
      setSearchText,
      sortOptions,
      selectedSortOption,
      sortKey,
      setSortKey,
      databaseOptions: equivalenceDatabaseOptions,
      selectedDatabaseOption: selectedEquivalenceDatabaseOption,
      databaseKey: equivalenceDatabaseKey,
      setDatabaseKey: setEquivalenceDatabaseKey,
      results: inlineFoodResults,
      isLoading: inlineFoodSearch.isLoading,
      loadingSessionId: inlineFoodSearch.loadingSessionId,
      showRowQuantityControl: false,
      getRowKey: (food, index) =>
        String(food?.id || food?.code || `${getFoodStableKey(food, language)}-${index}`),
      getRowTitle: (food) => getLocalizedFoodName(food, language),
      getRowSubtitle: (food) => getIngredientSourceLabel(food, t),
      getRowReferenceText: (food) =>
        formatQuantityInGramsText(getInitialFoodQuantityInGrams(food)),
      getRowTotals: (food) => {
        return getNutritionTotalsForQuantity(
          food,
          getInitialFoodQuantityInGrams(food),
        );
      },
      canSelectRow: () => true,
      onSelectRow: handleAddFood,
      onCreateFood: openAddFoodModal,
      emptyText: t("noIngredientResults-string"),
      compactLayout,
    }),
    [
      compactLayout,
      equivalenceDatabaseKey,
      equivalenceDatabaseOptions,
      focusSearchInput,
      handleAddFood,
      inputPressingRef,
      inlineFoodSearch.isLoading,
      inlineFoodSearch.loadingSessionId,
      inlineFoodResults,
      language,
      openAddFoodModal,
      searchText,
      selectedEquivalenceDatabaseOption,
      selectedSortOption,
      sortKey,
      sortOptions,
      t,
    ],
  );
  const mainPickerPanelProps = useMemo(
    () => ({
      t,
      searchInputRef,
      inputPressingRef,
      focusSearchInput,
      searchText,
      setSearchText,
      sortOptions,
      selectedSortOption,
      sortKey,
      setSortKey,
      databaseOptions: mainFilterOptions,
      selectedDatabaseOption: selectedMainFilterOption,
      databaseKey: pickerFilterKey,
      setDatabaseKey: setPickerFilterKey,
      results: mainPickerResults,
      isLoading: mainFoodSearch.isLoading,
      loadingSessionId: mainFoodSearch.loadingSessionId,
      showRowQuantityControl: false,
      getRowKey: (item, index) =>
        String(item?.key || item?.id || `picker-item-${index}`),
      getRowTitle: (item) => item?.title ?? "",
      getRowSubtitle: (item) => item?.subtitle ?? "",
      getRowEntityKind: (item) => item?.entityKind ?? "food",
      getRowEntityLabel: (item) =>
        item?.entityKind === "equivalent"
          ? t("equivalentResultType-string", "Equivalent")
          : item?.entityKind === "recipe"
            ? t("recipeResultType-string", "Recipe")
            : null,
      getRowPreviewText: (item) => item?.previewText ?? "",
      getRowReferenceText: () => "",
      getRowTotals: (item) => item?.nutrients ?? null,
      canSelectRow: () => true,
      onSelectRow: handleAddPickerItem,
      onCreateFood: openAddFoodModal,
      compactLayout,
      emptyText: t("noPickerResults-string", "No results match this search"),
      searchPlaceholder: t(
        "searchFoodEquivalentRecipe-string",
        "Search food, equivalent, or recipe",
      ),
    }),
    [
      compactLayout,
      handleAddPickerItem,
      inputPressingRef,
      mainFilterOptions,
      mainFoodSearch.isLoading,
      mainFoodSearch.loadingSessionId,
      mainPickerResults,
      openAddFoodModal,
      pickerFilterKey,
      searchText,
      selectedMainFilterOption,
      selectedSortOption,
      sortKey,
      sortOptions,
      t,
      focusSearchInput,
    ],
  );
  const shouldRenderInlineMainAddFoodPicker =
    !mealPlanEmbed &&
    isFoodPickerOpen &&
    !equivalenceTargetId &&
    option.items.length > 0;
  const inlineMainAddFoodTargetId = shouldRenderInlineMainAddFoodPicker
    ? option.items[option.items.length - 1]?.id ?? null
    : null;
  const shouldDetachMainFoodPickerFromDraggedRow =
    isFoodDragActive &&
    inlineMainAddFoodTargetId != null &&
    inlineMainAddFoodTargetId === activeFoodDragItemId;
  const shouldRenderStandaloneAddFoodSlot =
    !equivalenceTargetId &&
    (
      mealPlanEmbed ||
      !isFoodPickerOpen ||
      option.items.length === 0 ||
      shouldDetachMainFoodPickerFromDraggedRow
    );
  const isFoodDragTarget =
    foodDragTarget?.mealId === mealId && foodDragTarget?.optionId === option.id;
  const renderedItems = Array.isArray(option.items) ? option.items : [];
  const nonActiveItems =
    isFoodDragActive && activeFoodDragItemId
      ? renderedItems.filter((item) => item?.id !== activeFoodDragItemId)
      : renderedItems;
  const placeholderInsertionIndex = isFoodDragTarget
    ? Math.max(
        0,
        Math.min(
          Number(foodDragTarget?.insertionIndex ?? 0) || 0,
          nonActiveItems.length,
        ),
      )
    : -1;
  const shouldRenderEmptyDropZone =
    mealPlanEmbed &&
    nonActiveItems.length === 0 &&
    isFoodDragActive &&
    !isFoodDragTarget;

  const renderFoodPlaceholderIfNeeded = (insertionIndex) => {
    if (!isFoodDragTarget || insertionIndex !== placeholderInsertionIndex) {
      return null;
    }

    return <MealFoodPlaceholder height={foodDragPlaceholderHeight} />;
  };

  let renderedNonActiveIndex = 0;
  const foodRows = renderedItems.map((item) => {
    const originalIndex = option.items.findIndex(
      (candidateItem) => candidateItem?.id === item?.id,
    );
    const isDraggingActiveItem = item.id === activeFoodDragItemId;
    const placeholderBeforeRow = !isDraggingActiveItem
      ? renderFoodPlaceholderIfNeeded(renderedNonActiveIndex)
      : null;
    const row = (
      <DraggableMealFoodRow
        client={client}
        mealId={mealId}
        item={item}
        index={originalIndex}
        itemCount={nonActiveItems.length}
        optionId={option.id}
        compactLayout={compactLayout}
        equivalenceTargetId={equivalenceTargetId}
        setEquivalenceTargetId={setEquivalenceTargetId}
        onInlinePickerOpenChange={onInlinePickerOpenChange}
        updateItemQuantity={updateItemQuantity}
        updateItemUnit={updateItemUnit}
        removeItem={removeItem}
        updateEquivalenceQuantity={updateEquivalenceQuantity}
        updateEquivalenceUnit={updateEquivalenceUnit}
        removeEquivalence={removeEquivalence}
        equivalencePickerPanelProps={equivalencePickerPanelProps}
        equivalencePickerPanelRootRef={equivalencePickerPanelRootRef}
        isMainAddFoodTarget={
          item.id === inlineMainAddFoodTargetId &&
          !shouldDetachMainFoodPickerFromDraggedRow
        }
        mainPickerPanelProps={mainPickerPanelProps}
        mainPickerPanelRootRef={mainPickerPanelRootRef}
        closeMainFoodPicker={() => setFoodPickerOpen(false)}
        isDraggingActiveItem={isDraggingActiveItem}
        registerFoodRowNode={registerFoodRowNode}
        getFoodDragHandleProps={getFoodDragHandleProps}
        t={t}
      />
    );

    if (!isDraggingActiveItem) {
      renderedNonActiveIndex += 1;
    }

    return (
      <React.Fragment key={item.id}>
        {placeholderBeforeRow}
        {row}
      </React.Fragment>
    );
  });

  return (
    <View
      style={[
        styles.optionCard,
        !showHeader ? styles.optionCardEmbedded : null,
        isFoodDragActive ? styles.optionCardFoodDragActive : null,
      ]}
    >
      {showHeader ? (
        <View style={styles.optionHeader}>
          <View style={styles.optionHeaderMain}>
            <View style={styles.optionNameWrap}>
              <Text style={styles.optionNameText} numberOfLines={1}>
                {option.name?.trim() ||
                  t("nutritionPlanOptionName-string", "Option name")}
              </Text>
            </View>
            {isDefaultOption ? (
              <View style={styles.defaultBadge}>
                <Text style={styles.defaultBadgeText}>
                  {t("default-string")}
                </Text>
              </View>
            ) : null}
            <Text style={styles.optionKcal}>
              {Math.round(getMealOptionTotals(option).calories)} kcal
            </Text>
            <HoverableTouchableOpacity
              activeOpacity={0.85}
              onPress={() => {
                if (isFoodPickerOpen) {
                  setFoodPickerOpen(false);
                  return;
                }

                openMainFoodPicker();
              }}
              style={styles.iconButton}
              hoverStyle={styles.iconButtonHover}
            >
              <Ionicons
                name={isFoodPickerOpen ? "chevron-up-outline" : "add-outline"}
                size={16}
                color={Colors.bubbleColor}
              />
            </HoverableTouchableOpacity>
            <HoverableTouchableOpacity
              activeOpacity={0.85}
              onPress={() => removeOption(option.id)}
              style={styles.removeIconButton}
              hoverStyle={styles.removeIconButtonHover}
            >
              <Ionicons name="trash-outline" size={16} color={Colors.darkgrayColor} />
            </HoverableTouchableOpacity>
          </View>
        </View>
      ) : null}

      <View
        ref={(node) => registerFoodOptionBodyNode?.(mealId, option.id, node)}
        style={[
          styles.optionBody,
          showHeader ? styles.optionBodyHeaderMode : null,
          mealPlanEmbed && !showHeader ? styles.optionBodyMealPlanEmbed : null,
          isFoodDragTarget ? styles.optionBodyDropTargetActive : null,
        ]}
      >
        <div style={StyleSheet.flatten(styles.optionItemsList)}>
          {renderedItems.length > 0 ? (
            foodRows
          ) : (
            <>
              {renderFoodPlaceholderIfNeeded(0)}
              <EmptyMealOptionDropZone
                visible={shouldRenderEmptyDropZone}
                compactLayout={compactLayout}
                isActive={isFoodDragTarget}
                t={t}
              />
            </>
          )}
          {renderedItems.length > 0
            ? renderFoodPlaceholderIfNeeded(nonActiveItems.length)
            : null}
        </div>

        {shouldRenderStandaloneAddFoodSlot ? (
          <View
            style={[
              styles.addFoodSlot,
              mealPlanEmbed ? styles.addFoodSlotMealPlan : null,
            ]}
          >
            {isFoodPickerOpen ? (
              <View
                style={[
                  styles.inlinePickerWrap,
                  mealPlanEmbed ? styles.inlinePickerWrapMealPlan : null,
                ]}
              >
                <FoodPickerPanel
                  {...mainPickerPanelProps}
                  panelRootRef={mainPickerPanelRootRef}
                  title={t("addFood-string")}
                  subtitle={t(
                    "nutritionPlanAddPickerSubtitle-string",
                    "Search foods, equivalents, and recipes and add them to this option.",
                  )}
                  onClose={() => setFoodPickerOpen(false)}
                  isInline={true}
                  topMarginOverride={4}
                  bottomMarginOverride={-12}
                />
              </View>
            ) : (
              <HoverableTouchableOpacity
                activeOpacity={0.85}
                onPress={openMainFoodPicker}
                style={styles.reopenFoodBrowserButton}
                hoverStyle={styles.reopenFoodBrowserButtonHover}
              >
                <AntDesign name="plus" size={13} color={Colors.bubbleColor} />
                <Text style={styles.reopenFoodBrowserButtonText}>
                  {t("addNewIngredient-string")}
                </Text>
              </HoverableTouchableOpacity>
            )}
          </View>
        ) : null}
      </View>

      {addFoodModalSession.isVisible ? (
        <AddFoodModal
          key={addFoodModalSession.sessionKey}
          isVisible={true}
          onClose={closeAddFoodModal}
          onSaved={handleFoodCreated}
          coach={coach}
          food={null}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  optionCard: {
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    overflow: "hidden",
  },
  optionCardEmbedded: {
    borderWidth: 0,
    borderRadius: 0,
    backgroundColor: "transparent",
    overflow: "visible",
  },
  optionCardFoodDragActive: {
    overflow: "visible",
  },
  optionHeader: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: "rgba(63, 123, 141, 0.04)",
    borderBottomWidth: 0.5,
    borderBottomColor: Colors.borderColor,
  },
  optionHeaderMain: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  optionNameWrap: {
    flex: 1,
    minWidth: 180,
  },
  optionNameText: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  defaultBadge: {
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: "rgba(63, 123, 141, 0.12)",
  },
  defaultBadgeText: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.bubbleColor,
    textTransform: "uppercase",
  },
  optionKcal: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  optionBody: {
    paddingHorizontal: 0,
    paddingBottom: 4,
  },
  optionBodyHeaderMode: {
    paddingHorizontal: 14,
  },
  optionBodyMealPlanEmbed: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  optionBodyDropTargetActive: {
    backgroundColor: "transparent",
    borderRadius: 16,
  },
  optionItemsList: {
    position: "relative",
    zIndex: 2,
    overflow: "visible",
  },
  foodPlaceholder: {
    marginTop: 4,
    marginBottom: 6,
    borderRadius: 12,
    borderWidth: 0,
    backgroundColor: "transparent",
  },
  emptyDropZone: {
    marginTop: 4,
    marginBottom: 6,
    minHeight: 92,
    borderRadius: 14,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "rgba(63, 123, 141, 0.34)",
    backgroundColor: "rgba(63, 123, 141, 0.045)",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
    paddingVertical: 18,
    gap: 8,
  },
  emptyDropZoneCompact: {
    minHeight: 84,
    paddingHorizontal: 14,
  },
  emptyDropZoneActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  emptyDropZoneIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(63, 123, 141, 0.12)",
  },
  emptyDropZoneIconWrapActive: {
    backgroundColor: "rgba(63, 123, 141, 0.18)",
  },
  emptyDropZoneTitle: {
    ...Fonts.medium13,
    color: Colors.blackColor,
    textAlign: "center",
  },
  iconButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
  iconButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  removeIconButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  removeIconButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  rowActionGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: 80,
    flexShrink: 0,
    justifyContent: "flex-end",
  },
  rowActionGroupCompact: {
    width: "auto",
  },
  rowActionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
  rowActionButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  rowActionButtonActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  removeIngredientButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
  removeIngredientButtonHover: {
    backgroundColor: "rgba(240, 68, 56, 0.05)",
    borderColor: "rgba(240, 68, 56, 0.18)",
  },
  addFoodSlot: {
    marginTop: 0,
    position: "relative",
    zIndex: 1,
  },
  addFoodSlotMealPlan: {
    marginTop: 6,
  },
  inlinePickerWrap: {
    paddingBottom: 0,
  },
  inlinePickerWrapMealPlan: {
    paddingBottom: 12,
  },
  selectedIngredientEquivalencesRow: {
    width: "100%",
    marginTop: EQUIVALENCE_TREE_CARD_VERTICAL_PADDING,
    gap: 0,
  },
  selectedEquivalenceCard: {
    paddingVertical: EQUIVALENCE_TREE_CARD_VERTICAL_PADDING,
  },
  equivalenceTreeRowContainer: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  equivalenceTreeRowContainerLast: {
    marginBottom: -EQUIVALENCE_TREE_CARD_VERTICAL_PADDING,
  },
  equivalenceTreeLineContainer: {
    width: 34,
    position: "relative",
    flexShrink: 0,
  },
  equivalenceTreeVerticalLine: {
    position: "absolute",
    left: 11.5,
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: Colors.borderColor,
  },
  equivalenceTreeVerticalLineFirst: {
    top: -EQUIVALENCE_TREE_CARD_VERTICAL_PADDING,
  },
  equivalenceTreeVerticalLineLast: {
    bottom: "50%",
  },
  equivalenceTreeHorizontalLine: {
    position: "absolute",
    left: 11.5,
    top: "50%",
    marginTop: -0.5,
    width: 14,
    height: 1,
    backgroundColor: Colors.borderColor,
  },
  equivalenceTreeDot: {
    position: "absolute",
    left: 24.5,
    top: "50%",
    marginTop: -3,
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.whiteColor,
    borderWidth: 1.5,
    borderColor: Colors.bubbleColor,
  },
  equivalenceTreeCardWrap: {
    flex: 1,
    minWidth: 0,
  },
  equivalenceIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(63, 123, 141, 0.08)",
    flexShrink: 0,
  },
  equivalenceRemoveButton: {
    borderWidth: 0,
  },
  reopenFoodBrowserButton: {
    marginTop: 4,
    minHeight: 42,
    borderRadius: 12,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transitionProperty: "opacity",
    transitionDuration: "160ms",
    transitionTimingFunction: "ease",
  },
  reopenFoodBrowserButtonHover: {
    opacity: 0.82,
  },
  reopenFoodBrowserButtonText: {
    ...Fonts.medium13,
    color: Colors.bubbleColor,
  },
});
